(() => {
  if (customElements.get('cg-search-panel')) return;
  const normalise = (text) => text.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
  let aliasIndex;
  async function loadAliasIndex() {
    if (aliasIndex) return aliasIndex;
    aliasIndex = (async () => {
      const endpoint = document.querySelector('[data-alias-url]')?.dataset.aliasUrl;
      if (!endpoint) throw new Error('Missing name index');
      let url = new URL(endpoint, window.location.origin);
      const records = [];
      const visited = new Set();
      while (url) {
        url.searchParams.set('section_id', 'cg-search-aliases');
        if (url.origin !== window.location.origin || visited.has(url.href)) throw new Error('Invalid name index');
        visited.add(url.href);
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 5000);
        let html;
        try {
          const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
          if (!response.ok) throw new Error('Name index unavailable');
          html = await response.text();
        } finally { clearTimeout(timeout); }
        const index = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cg-alias-index]');
        if (!index) throw new Error('Missing name index');
        for (const product of index.querySelectorAll('[data-cg-alias-product]')) {
          const names = JSON.parse(product.dataset.names).filter(name => typeof name === 'string' && name.trim());
          if (names.length) records.push({ title: product.dataset.title, names: names.map(normalise) });
        }
        url = index.dataset.next ? new URL(index.dataset.next, url) : null;
      }
      return records;
    })().catch(error => { aliasIndex = null; throw error; });
    return aliasIndex;
  }
  async function resolveTerm(term) {
    const query = term.trim();
    if (query.length < 2 || /[:"()\\]|\b(?:AND|OR|NOT)\b/.test(query)) return query;
    const words = normalise(query).split(' ').filter(Boolean);
    if (!words.length) return query;
    const matches = (name) => words.every(word => name.split(' ').some(part => part.startsWith(word)));
    const records = await loadAliasIndex();
    const titles = [...new Set(records.filter(record => record.names.some(matches) && !matches(normalise(record.title))).map(record => record.title))];
    return query + titles.map(title => {
      const parts = title.match(/[\p{L}\p{N}]+/gu) || [];
      return parts.length ? ' OR (' + parts.map(part => 'title:' + part).join(' AND ') + ')' : '';
    }).join('');
  }
  document.addEventListener('submit', async event => {
    const form = event.target.closest('[data-cg-alias-form]');
    if (!form || event.defaultPrevented) return;
    event.preventDefault();
    if (form.dataset.resolving === 'true') return;
    const query = new FormData(form).get('q') || '';
    if (!query.trim()) return;
    form.dataset.resolving = 'true';
    form.setAttribute('aria-busy', 'true');
    const url = new URL(form.action, window.location.origin);
    for (const [key, value] of new FormData(form)) url.searchParams.set(key, value);
    try { url.searchParams.set('q', await resolveTerm(query)); }
    catch { url.searchParams.set('q', query); }
    window.location.assign(url.href);
  });
  window.addEventListener('pageshow', () => {
    document.querySelectorAll('[data-cg-alias-form]').forEach(form => {
      delete form.dataset.resolving;
      form.removeAttribute('aria-busy');
    });
  });
  const pageForm = document.querySelector('.cg-search-page [data-cg-alias-form]');
  if (pageForm) {
    const url = new URL(window.location.href);
    const original = url.searchParams.get('q');
    if (original) resolveTerm(original).then(resolved => {
      if (resolved !== original) { url.searchParams.set('q', resolved); window.location.replace(url.href); }
    }).catch(() => {});
  }
  class CGSearchPanel extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.input = this.querySelector('[data-cg-query]');
      this.form = this.querySelector('[data-cg-query-form]');
      this.results = this.querySelector('[data-cg-search-results]');
      this.start = this.querySelector('[data-cg-search-start]');
      this.status = this.querySelector('[data-cg-search-status]');
      this.drawer = this.closest('sidebar-drawer');
      this.sequence = 0;
      this.cache = new Map();
      this.normalise = normalise;
      this.shortcuts = new Map(Array.from(this.querySelectorAll('[data-cg-collection]')).map(a => [this.normalise(a.dataset.cgCollection), a.href]));
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.input.addEventListener('input', () => {
        this.cancelRequest();
        this.results.replaceChildren();
        this.results.hidden = true;
        this.status.textContent = '';
        const term = this.input.value.trim();
        this.start.hidden = term.length >= 2;
        if (term.length < 2) return;
        this.timer = setTimeout(() => this.search(term), 220);
      }, options);
      this.input.addEventListener('keydown', event => {
        if (event.key === 'ArrowDown') {
          const first = this.results.querySelector('[data-cg-result-link]');
          if (first) { event.preventDefault(); first.focus(); }
        }
      }, options);
      this.addEventListener('click', event => {
        const quick = event.target.closest('[data-cg-term],[data-cg-collection]');
        if (!quick || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.input.value = quick.dataset.cgTerm || quick.dataset.cgCollection;
        this.search(this.input.value);
        this.input.focus();
      }, options);
      this.form.addEventListener('submit', event => {
        const category = this.shortcuts.get(this.normalise(this.input.value));
        if (category) { event.preventDefault(); window.location.assign(category); }
      }, options);
      document.addEventListener('click', event => {
        const opener = event.target.closest('[data-js-open-search-drawer]');
        if (opener) this.opener = opener;
      }, { signal: this.events.signal, capture: true });
      this.drawer.addEventListener('keydown', event => {
        if (event.key !== 'Tab') return;
        const nodes = Array.from(this.drawer.querySelectorAll('a[href],button:not([disabled]),input:not([type="hidden"]):not([disabled]),select:not([disabled]),[tabindex="0"]')).filter(el => el.getClientRects().length && el.tabIndex >= 0);
        const first = nodes[0], last = nodes[nodes.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }, options);
      this.observer = new MutationObserver(() => {
        const isOpen = this.drawer.classList.contains('sidebar--opened');
        if (isOpen === this.wasOpen) return;
        this.wasOpen = isOpen;
        if (isOpen) {
          loadAliasIndex().catch(() => {});
          this.input.focus();
          if (this.input.value.trim().length >= 2 && this.results.hidden) this.search(this.input.value.trim());
        }
        else {
          this.cancelRequest();
          this.results.removeAttribute('aria-busy');
          this.status.textContent = '';
          requestAnimationFrame(() => this.opener?.focus());
        }
      });
      this.observer.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });
    }
    cancelRequest() {
      clearTimeout(this.timer);
      this.controller?.abort();
      this.sequence += 1;
      this.results?.removeAttribute('aria-busy');
    }
    async search(term) {
      this.cancelRequest();
      if (term.trim().length < 2) return;
      const sequence = this.sequence;
      const category = this.shortcuts.get(this.normalise(term));
      this.start.hidden = true;
      this.results.hidden = true;
      this.results.replaceChildren();
      this.results.setAttribute('aria-busy', 'true');
      this.status.textContent = this.dataset.loading;
      this.controller = new AbortController();
      try {
        let resolved = term;
        if (!category) resolved = await resolveTerm(term).catch(() => term);
        if (sequence !== this.sequence) return;
        const expanded = resolved !== term;
        const url = new URL(category || (expanded ? this.form.action : this.dataset.suggestUrl), window.location.origin);
        url.searchParams.set('section_id', 'cg-search-suggestions');
        if (!category) {
          url.searchParams.set('q', resolved);
          if (expanded) {
            url.searchParams.set('type', 'product');
            url.searchParams.set('options[prefix]', 'last');
            url.searchParams.set('options[unavailable_products]', 'last');
          } else {
            url.searchParams.set('resources[type]', 'product,collection,article');
            url.searchParams.set('resources[limit]', '8');
            url.searchParams.set('resources[limit_scope]', 'each');
            url.searchParams.set('resources[options][unavailable_products]', 'last');
          }
        }
        const key = url.toString();
        let html = this.cache.get(key);
        if (!html) {
          const response = await fetch(key, { signal: this.controller.signal, credentials: 'same-origin' });
          if (!response.ok) throw new Error('Search unavailable');
          html = await response.text();
          if (sequence !== this.sequence) return;
          if (this.cache.size >= 12) this.cache.delete(this.cache.keys().next().value);
          this.cache.set(key, html);
        }
        if (sequence !== this.sequence) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cg-suggestions]');
        if (!parsed) throw new Error('Missing results');
        this.results.replaceChildren(document.importNode(parsed, true));
        this.results.hidden = false;
        this.status.textContent = this.dataset.count.replace('[count]', parsed.dataset.count || '0');
        this.querySelector('.cg-search-scroll').scrollTop = 0;
      } catch (error) {
        if (error.name === 'AbortError' || sequence !== this.sequence) return;
        this.status.textContent = this.dataset.error;
        this.start.hidden = false;
      } finally {
        if (sequence === this.sequence) this.results.removeAttribute('aria-busy');
      }
    }
    disconnectedCallback() {
      this.cancelRequest();
      this.events?.abort();
      this.observer?.disconnect();
      this.ready = false;
    }
  }
  customElements.define('cg-search-panel', CGSearchPanel);
  document.addEventListener('change', event => {
    const select = event.target.closest('[data-cg-variant]');
    if (!select) return;
    const option = select.selectedOptions[0];
    const card = select.closest('[data-cg-product]');
    card.querySelector('[data-cg-price]').textContent = option.dataset.price;
    const compare = card.querySelector('[data-cg-compare]');
    compare.textContent = option.dataset.compare || '';
    compare.hidden = !option.dataset.compare;
    if (option.dataset.image) {
      const image = card.querySelector('.cg-search-product-photo img');
      if (image) { image.removeAttribute('srcset'); image.src = option.dataset.image; }
    }
  });
  document.addEventListener('submit', async event => {
    const form = event.target.closest('[data-cg-search-add]');
    if (!form) return;
    event.preventDefault();
    if (form.dataset.busy === 'true' || !form.reportValidity()) return;
    const id = new FormData(form).get('id');
    if (!id) return;
    const button = form.querySelector('[type="submit"]');
    const error = form.querySelector('[data-cg-add-error]');
    const label = button.textContent;
    form.dataset.busy = 'true';
    button.disabled = true;
    button.textContent = form.dataset.loading;
    error.hidden = true;
    let customerError = form.dataset.error;
    try {
      const response = await fetch(form.action.replace(/\/$/, '') + '.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id, quantity: 1 }] }) });
      const result = await response.json().catch(() => null);
      if (!response.ok || !result || result.status) {
        if (typeof result?.description === 'string') customerError = result.description;
        throw new Error('Cart request failed');
      }
      window.location.assign(form.dataset.cartUrl);
    } catch (failure) {
      error.textContent = customerError;
      error.hidden = false;
      form.dataset.busy = 'false';
      button.disabled = false;
      button.textContent = label;
    }
  });
})();
